/**
 * useLandingMotion — wires every GSAP-driven micro-interaction on the Landing
 * page from one place. Mount this hook once at the top of Landing.tsx.
 *
 * What it owns:
 *  - Section reveal stagger via ScrollTrigger
 *  - Hero CTA idle pulse (disabled past hero)
 *  - Pricing cards: hover lift handled by Tailwind; the .highlight card gets
 *    a baseline elevated transform; primary pricing CTAs get magnetic
 *    cursor attraction
 *  - All buttons: press-feedback bounce on click
 *  - Navbar: scrolled-state class toggle past 100px
 *  - Founder photo: gentle scroll parallax + hover glow
 *  - Mascot continuous float (Y ±2px) applied to a CSS hook
 *
 * Everything is no-op when prefers-reduced-motion is set, except:
 *   - Instant fade-in still applied so users see content (opacity 0->1 in 1ms)
 *   - Press-feedback bounce on click is preserved (it's a usability cue, not
 *     decoration)
 *
 * All ScrollTrigger instances and tweens are torn down on unmount via
 * gsap.context().revert().
 */

import { useEffect } from 'react';
import { gsapWithScrollTrigger, motionEnabled, ScrollTrigger } from '../../lib/motion';

export function useLandingMotion(): void {
  useEffect(() => {
    const gsap = gsapWithScrollTrigger();
    const reduced = !motionEnabled();

    // IMPORTANT: gsap.context() runs this callback SYNCHRONOUSLY during
    // `const ctx = ...` initialization. That means `ctx` is in the temporal
    // dead zone while the callback runs. To register cleanup callbacks we
    // MUST use the `self` argument (which IS the context instance), not the
    // outer `ctx` variable. Touching `ctx` from inside throws ReferenceError.
    const ctx = gsap.context((self) => {
      // ─── 1. Section reveal stagger ───────────────────────────────
      // Reveals any element marked with data-anima-reveal. Children
      // marked data-anima-reveal-child are staggered inside.
      const sections = gsap.utils.toArray<HTMLElement>('[data-anima-reveal]');
      sections.forEach((section) => {
        // Start from initial hidden state regardless of reduced motion so
        // there's no FOUC.
        gsap.set(section, { opacity: 0, y: reduced ? 0 : 24 });

        const children = section.querySelectorAll<HTMLElement>('[data-anima-reveal-child]');
        if (children.length > 0) {
          gsap.set(Array.from(children), { opacity: 0, y: reduced ? 0 : 16 });
        }

        ScrollTrigger.create({
          trigger: section,
          start: 'top 80%',
          once: true,
          onEnter: () => {
            gsap.to(section, {
              opacity: 1,
              y: 0,
              duration: reduced ? 0.001 : 0.6,
              ease: 'power2.out',
            });
            if (children.length > 0) {
              gsap.to(Array.from(children), {
                opacity: 1,
                y: 0,
                duration: reduced ? 0.001 : 0.5,
                ease: 'power2.out',
                stagger: reduced ? 0 : 0.08,
                delay: reduced ? 0 : 0.1,
              });
            }
          },
        });
      });

      // ─── 2. Hero primary CTA — idle attention pulse ────────────────
      const heroCta = document.querySelector<HTMLElement>('[data-anima-hero-cta]');
      if (heroCta && !reduced) {
        const pulseTl = gsap.timeline({ repeat: -1, repeatDelay: 6.8 });
        pulseTl.to(heroCta, { scale: 1.03, duration: 0.6, ease: 'sine.inOut' })
               .to(heroCta, { scale: 1.0,  duration: 0.6, ease: 'sine.inOut' });

        // Stop pulsing once the user scrolls past the hero.
        ScrollTrigger.create({
          trigger: heroCta,
          start: 'bottom top',
          onEnter: () => { pulseTl.pause(0); },
          onLeaveBack: () => { pulseTl.resume(); },
        });
      }

      // ─── 3. Continuous float for floating mascots ──────────────────
      const floats = gsap.utils.toArray<HTMLElement>('[data-anima-float]');
      if (!reduced && floats.length > 0) {
        floats.forEach((el, i) => {
          gsap.to(el, {
            y: '+=4',
            duration: 4,
            ease: 'sine.inOut',
            repeat: -1,
            yoyo: true,
            delay: i * 0.25,
          });
        });
      }

      // ─── 4. Navbar scroll behaviour ────────────────────────────────
      const navbar = document.querySelector<HTMLElement>('[data-anima-navbar]');
      if (navbar) {
        const onScroll = () => {
          if (window.scrollY > 100) navbar.classList.add('navbar-scrolled');
          else navbar.classList.remove('navbar-scrolled');
        };
        window.addEventListener('scroll', onScroll, { passive: true });
        onScroll();
        // gsap.context cleanup doesn't manage window listeners — register
        // a teardown manually via the context's selector array.
        self.add(() => () => window.removeEventListener('scroll', onScroll));
      }

      // ─── 5. Founder photo — scroll parallax + hover scale ─────────
      const photoFrame = document.querySelector<HTMLElement>('.founder-photo-frame');
      const photoImg = document.querySelector<HTMLElement>('.founder-photo');
      if (photoFrame && photoImg && !reduced) {
        gsap.fromTo(
          photoImg,
          { y: -8 },
          {
            y: 8,
            ease: 'none',
            scrollTrigger: {
              trigger: photoFrame,
              start: 'top bottom',
              end: 'bottom top',
              scrub: true,
            },
          },
        );
      }

      // ─── 6. Pricing — magnetic CTAs ────────────────────────────────
      const pricingCtas = gsap.utils.toArray<HTMLElement>('[data-anima-magnetic]');
      if (!reduced) {
        pricingCtas.forEach((btn) => {
          const onMove = (e: MouseEvent) => {
            const rect = btn.getBoundingClientRect();
            const cx = rect.left + rect.width / 2;
            const cy = rect.top + rect.height / 2;
            const dx = e.clientX - cx;
            const dy = e.clientY - cy;
            const dist = Math.hypot(dx, dy);
            // Only attract within 40px of the button edge.
            const radius = Math.max(rect.width, rect.height) / 2 + 40;
            if (dist > radius) return;
            gsap.to(btn, { x: dx * 0.2, y: dy * 0.2, duration: 0.25, ease: 'power2.out' });
          };
          const onLeave = () => {
            gsap.to(btn, { x: 0, y: 0, duration: 0.45, ease: 'elastic.out(1, 0.4)' });
          };
          btn.addEventListener('mousemove', onMove);
          btn.addEventListener('mouseleave', onLeave);
          self.add(() => () => {
            btn.removeEventListener('mousemove', onMove);
            btn.removeEventListener('mouseleave', onLeave);
          });
        });
      }

      // ─── 7. Click press-feedback on all marked CTAs ────────────────
      const pressBtns = gsap.utils.toArray<HTMLElement>('[data-anima-press]');
      pressBtns.forEach((btn) => {
        const onClick = () => {
          gsap.fromTo(
            btn,
            { scale: 0.96 },
            { scale: 1, duration: 0.3, ease: 'back.out(2)' },
          );
        };
        btn.addEventListener('click', onClick);
        self.add(() => () => btn.removeEventListener('click', onClick));
      });

      // ─── 8. Refresh ScrollTrigger after layout settles ─────────────
      // The Landing page has fonts + images that shift layout post-mount.
      const refreshTimer = window.setTimeout(() => ScrollTrigger.refresh(), 400);
      self.add(() => () => window.clearTimeout(refreshTimer));
    });

    return () => ctx.revert();
  }, []);
}
